import React, { useEffect, useState } from "react";
import {
  Layout,
  Card,
  Form,
  Button,
  Spin,
  Select,
  InputNumber,
  Row,
  Col,
  Input,
  Typography,
  Divider,
} from "antd";
import { RollbackOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";

import { getAPI, postAPI, displayMessage } from "../../utils/common";
import {
  KITCHEN_ISSUE_LIST,
  KITCHEN_ISSUE_DETAIL,
  KITCHEN_ISSUE_UPDATE,
} from "../../constants/api";

const { Title, Text } = Typography;

function KitchenIssueUpdate() {
  const { Content } = Layout;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const [issues, setIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);

  useEffect(() => {
    fetchIssues();
  }, []);

  const fetchIssues = () => {
    getAPI(
      KITCHEN_ISSUE_LIST,
      (res) => {
        if (res.status) setIssues(res.data);
      },
      () => {},
    );
  };

  const onSelectIssue = (id) => {
    setLoading(true);

    postAPI(
      KITCHEN_ISSUE_DETAIL,
      { id },
      (res) => {
        if (res.status) {
          // 🔥 ONLY NOT UPDATED ITEMS
          const filteredItems = res.data.items.filter(
            (i) => parseInt(i.remaining_qty ?? 0) === 0,
          );

          setSelectedIssue({
            ...res.data,
            items: filteredItems,
          });

          form.setFieldsValue({
            issue_id: res.data.id,
            items: filteredItems.map((i) => ({
              id: i.id,
              item_name: i.item?.name,
              item_code: i.item?.item_code,
              qty: i.qty,
              remaining_qty: 0,
            })),
          });
        }
        setLoading(false);
      },
      () => setLoading(false),
    );
  };

  const clearForm = () => {
    form.resetFields();
    setSelectedIssue(null);
  };

  const submitUpdate = (values) => {
    // Block if already updated
    const alreadyUpdated = selectedIssue.items.some(
      (item) => (item.remaining_qty ?? 0) > 0,
    );

    if (alreadyUpdated) {
      displayMessage(
        "error",
        "This issue is already updated. You cannot update it again.",
      );
      return;
    }
    setLoading(true);

    const payload = {
      issue_id: selectedIssue.id,
      items: values.items.map((i) => ({
        id: i.id,
        remaining_qty: i.remaining_qty,
      })),
    };

    postAPI(
      KITCHEN_ISSUE_UPDATE,
      payload,
      (res) => {
        displayMessage(res.status ? "success" : "error", res.message);
        setLoading(false);
      },
      () => {
        setLoading(false);
        displayMessage("error", "Update failed");
      },
    );
  };

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card title="Manage Kitchen Remaining">
          <Spin spinning={loading}>
            {/* ===================== */}
            {/* STEP 1: ISSUE SELECT */}
            {/* ===================== */}
            <Card size="small" style={{ marginBottom: 15 }}>
              <Title level={5}>Step 1: Select Issue</Title>

              <Form layout="vertical">
                <Form.Item label="Choose Issue ID">
                  <Select
                    placeholder="Select Issue"
                    onChange={onSelectIssue}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                    options={issues.map((i) => ({
                      value: i.id,
                      label: `#${i.id} - ${dayjs(i.issue_date).format("DD MMM YYYY")}`,
                    }))}
                  />
                </Form.Item>
              </Form>
            </Card>

            {/* ===================== */}
            {/* STEP 2: ISSUE DETAILS */}
            {/* ===================== */}
            {selectedIssue && selectedIssue.items.length === 0 && (
              <Card>
                <Text type="success">
                  ✅ All items already updated. Nothing pending.
                </Text>
              </Card>
            )}
            {selectedIssue && selectedIssue.items.length > 0 && (
              <Form form={form} layout="vertical" onFinish={submitUpdate}>
                <Card size="small" title="Step 2: Update Issue Items">
                  {selectedIssue.items.map((item, index) => (
                    <Card
                      key={item.id}
                      size="small"
                      style={{ marginBottom: 12, background: "#fafafa" }}
                    >
                      <Row gutter={16} align="middle">
                        {/* ITEM INFO */}
                        <Col span={6}>
                          <Form.Item label="Item">
                            <Input
                              disabled
                              value={`${item.item?.item_code} - ${item.item?.name}`}
                            />
                          </Form.Item>
                        </Col>

                        {/* ISSUED QTY */}
                        <Col span={4}>
                          <Form.Item label="Issued Qty">
                            <InputNumber
                              disabled
                              value={item.qty}
                              suffix={`${item?.item?.unit ? `(${item.item?.unit})` : ""}`}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </Col>

                        {/* REMAINING QTY (EDITABLE) */}
                        <Col span={6}>
                          <Form.Item
                            label="Remaining Qty"
                            name={["items", index, "remaining_qty"]}
                            initialValue={item.remaining_qty ?? 0}
                            rules={[
                              { required: true, message: "Required" },
                              {
                                validator: (_, value) => {
                                  if (value > item.qty) {
                                    return Promise.reject(
                                      "Remaining cannot exceed issued qty",
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <InputNumber
                              min={0}
                              max={item.qty}
                              suffix={`${item?.item?.unit ? `(${item.item?.unit})` : ""}`}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </Col>

                        {/* HIDDEN ID */}
                        <Form.Item
                          name={["items", index, "id"]}
                          initialValue={item.id}
                          hidden
                        >
                          <input />
                        </Form.Item>
                      </Row>
                    </Card>
                  ))}
                  <Row>
                    <Col span={24}>
                      <Form.Item style={{ float: "right", marginTop: 16 }}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                        >
                          Update Issue
                        </Button>{" "}
                        <Button
                          danger
                          onClick={clearForm}
                          disabled={!selectedIssue}
                        >
                          Clear
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Form>
            )}
          </Spin>
        </Card>
      </Content>
      <LayoutFooter />
    </Layout>
  );
}

export default KitchenIssueUpdate;
